import { CheckIcon, PencilIcon } from "@heroicons/react/24/outline";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import MarkdownEditor from "@via/components/markdown-editor/markdown-editor.tsx";
import Table from "@via/components/table/table.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { EMPTY_TABLE_MESSAGE, ROUTES } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { type ProcessDataInterface } from "@via/utils/interfaces.tsx";
import { type JSX, useCallback, useEffect, useState } from "react";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import { PipelineDetails } from "@via/pages/projects/pipelines/description/pipeline-details/pipeline-details.tsx";
import { EditPipelineDetails } from "@via/pages/projects/pipelines/description/pipeline-details/edit-pipeline-details.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";

const INITIAL_DESC_MESSAGE = `Create a description by clicking "Edit Description"`;
const INITIAL_SUMMARY_MESSAGE = `Create a summary by clicking "Edit Summary"`;
const isEmpty = (text: string): boolean => {
  return text === "" || text === INITIAL_DESC_MESSAGE;
};

const columnHelper = createColumnHelper<ProcessDataInterface>();

export default function PipelineDescription(): JSX.Element {
  const { pipelineDraft, selectedPipeline } = useGlobal();
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [isEditDetailsModalOpen, setIsEditDetailsModalOpen] = useState(false);
  const [summaryValue, setSummaryValue] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");
  const [processes, setProcesses] = useState<ProcessDataInterface[]>([]);
  const { setPipelineDraft } = useGlobal();
  const { increaseChangeCount } = useChangesDetect();

  useEffect(() => {
    if (pipelineDraft) {
      if (pipelineDraft?.summary) {
        setSummaryValue(pipelineDraft?.summary);
      }
      if (pipelineDraft?.description) {
        setDescriptionValue(pipelineDraft?.description);
      }
    }
  }, [pipelineDraft]);

  useEffect(() => {
    if (isEditingDescription && isEmpty(descriptionValue)) {
      setDescriptionValue("");
    }
  }, [isEditingDescription]);

  useEffect(() => {
    if (selectedPipeline) {
      const result: ProcessDataInterface[] = [];
      for (const [key, value] of Object.entries(selectedPipeline)) {
        if (key.includes("process_")) {
          result.push(value);
        }
      }
      setProcesses(result);
    }
  }, [selectedPipeline]);

  const onDescriptionChange = useCallback((value: string) => {
    setDescriptionValue(value);
    setPipelineDraft(prevData => ({
      ...prevData,
      description: value,
    }));
  }, []);

  const onSummaryChange = value => {
    setSummaryValue(value);
    setPipelineDraft(prevData => ({
      ...prevData,
      summary: value,
    }));
  };

  const handlePipelineDetailsEditClick = () => {
    setIsEditDetailsModalOpen(true);
  };

  const handleEditSummaryClick = useCallback(() => {
    increaseChangeCount();
    setIsEditingSummary(!isEditingSummary);
  }, [isEditingSummary]);

  const handleEditDescriptionClick = useCallback(() => {
    increaseChangeCount();
    setIsEditingDescription(!isEditingDescription);
  }, [isEditingDescription]);

  const getPipelineDescriptionContext = useCallback(() => {
    return {
      pipeline: {
        id: pipelineDraft?.id,
        name: pipelineDraft?.name,
        summary:
          summaryValue && summaryValue !== INITIAL_SUMMARY_MESSAGE
            ? summaryValue
            : null,
        description:
          descriptionValue && descriptionValue !== INITIAL_DESC_MESSAGE
            ? descriptionValue
            : null,
        version: pipelineDraft?.version,
        workflowStatus: pipelineDraft?.workflowStatus,
        origin: pipelineDraft?.origin,
        dateCreated: pipelineDraft?.dateCreated,
        dateModified: pipelineDraft?.dateModified,
        isEditingAllowed: pipelineDraft?.isEditingAllowed,
      },
      processes: processes.map(process => ({
        name: process.name,
        revId: process.rev_id,
        summary: process.summary,
      })),
      processCount: processes.length,
    };
  }, [pipelineDraft, summaryValue, descriptionValue, processes]);

  useRegisterCortexContext({
    routePattern: ROUTES.PIPELINE_DESCRIPTION,
    contextName: "Pipeline Description",
    getContext: getPipelineDescriptionContext,
    systemPrompt:
      "You are analyzing pipeline description page. Help answer questions about the pipeline, its summary, description, and processes.",
  });

  const columns = [
    columnHelper.accessor("name", {
      cell: info => info.getValue(),
      header: () => <span>Name</span>,
      size: 100,
    }),
    columnHelper.accessor("rev_id", {
      cell: info => info.getValue(),
      header: () => <span>Revision</span>,
      size: 10,
    }),
    columnHelper.accessor("summary", {
      cell: info => info.getValue(),
      header: () => <span>Description</span>,
      size: 300,
    }),
  ] as Array<ColumnDef<ProcessDataInterface>>;

  return (
    <main className="py-6 flex flex-col-reverse md:flex-row gap-4">
      <div className="w-full md:w-3/4">
        <div className="pb-6 mb-4 max-h-[330px] overflow-scroll rounded-md">
          <div className="flex flex-row items-center justify-start pb-2">
            <h2 className="via-h2">Summary&nbsp;</h2>
            {pipelineDraft?.isEditingAllowed && (
              <button
                data-testid="editPipelineSummaryButton"
                type="submit"
                className="ml-2 inline-flex items-center justify-center rounded-md px-3 text-sm font-semibold text-indigo-600 duration-200 ease-in-out hover:text-indigo-500"
                onClick={handleEditSummaryClick}>
                {isEditingSummary ? (
                  <>
                    <CheckIcon className="h-4 w-4 stroke-2" /> &nbsp;Done
                  </>
                ) : (
                  <>
                    <PencilIcon className="h-3 w-3 stroke-2" /> &nbsp;Edit
                    Summary
                  </>
                )}
              </button>
            )}
          </div>
          <textarea
            className={classNames(
              isEditingSummary
                ? "border-gray-300"
                : "resize-none border-[1px] border-gray-300 outline-none focus:ring-0 focus:ring-offset-0",
              "size-field max-h-[200px] rounded-lg w-full"
            )}
            onChange={e => onSummaryChange(e.target.value)}
            readOnly={!isEditingSummary}
            placeholder={INITIAL_SUMMARY_MESSAGE}
            value={summaryValue}
          />
        </div>
        <div className="pb-12 mb-4">
          <div className="flex flex-row items-center justify-start pb-2">
            <h2 className="via-h2">Description&nbsp;</h2>
            {pipelineDraft?.isEditingAllowed && (
              <button
                data-testid="editPipelineDescriptionButton"
                type="submit"
                className="ml-2 inline-flex items-center justify-center rounded-md px-3 text-sm font-semibold text-indigo-600 duration-200 ease-in-out hover:text-indigo-500"
                onClick={handleEditDescriptionClick}>
                {isEditingDescription ? (
                  <>
                    <CheckIcon className="h-4 w-4 stroke-2" /> &nbsp;Done
                  </>
                ) : (
                  <>
                    <PencilIcon className="h-3 w-3 stroke-2" /> &nbsp;Edit
                    Description
                  </>
                )}
              </button>
            )}
          </div>
          <MarkdownEditor
            value={descriptionValue}
            onChange={onDescriptionChange}
            isEditing={isEditingDescription}
          />
        </div>
        <div className="pb-12">
          {processes.length > 0 && (
            <>
              <h2 className="via-h2">Processes</h2>
              <Table
                data={processes}
                columns={columns}
                total={processes?.length || 0}
                applyColumnSize
                emptyMessage={EMPTY_TABLE_MESSAGE.PROCESSES}
              />
            </>
          )}
        </div>
      </div>
      <div className="w-full md:w-1/4">
        <PipelineDetails onEditClick={handlePipelineDetailsEditClick} />
        {isEditDetailsModalOpen ? (
          <EditPipelineDetails
            open={isEditDetailsModalOpen}
            setOpen={setIsEditDetailsModalOpen}
          />
        ) : null}
      </div>
    </main>
  );
}
